import { motion } from "framer-motion";
import { Sparkles, Route as RouteIcon } from "lucide-react";
import { AppScreenshot } from "@/sections/ttk/Shared";

const R = ({ children, d = 0, c = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.85, delay: d, ease: [0.16, 1, 0.3, 1] }}
    className={c}
  >
    {children}
  </motion.div>
);

/* -------------------------------------------------------------------- */
/* Phone plate · the phone + phone-side annotations                       */
/* -------------------------------------------------------------------- */
const PhonePlate = ({ src, alt, label, testid, callouts = [] }) => (
  <div className="relative">
    <div className="mx-auto w-[260px] md:w-[320px] lg:w-[360px]">
      <AppScreenshot src={src} alt={alt} data-testid={testid} scale={1.1} />
    </div>
    {label && (
      <p className="mt-4 text-center font-mono text-[9px] tracking-[0.24em] text-ash">
        {label}
      </p>
    )}
    {callouts.map((c, i) => (
      <motion.div
        key={i}
        initial={{ opacity: 0, y: 8 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7, delay: 0.3 + i * 0.15 }}
        style={{ ...c.pos }}
        className="pointer-events-none absolute hidden lg:block"
      >
        <div className="flex items-center gap-3">
          {c.side === "right" && (
            <span className="h-px w-10 bg-signal" aria-hidden="true" />
          )}
          <div className="rounded-md border border-signal/40 bg-ink/90 px-3 py-2 backdrop-blur-sm">
            <p className="font-mono text-[8px] tracking-[0.22em] text-signal">
              {c.tag}
            </p>
            <p className="mt-1 font-mono text-[8px] tracking-[0.14em] text-white/90">
              {c.text}
            </p>
          </div>
          {c.side === "left" && (
            <span className="h-px w-10 bg-signal" aria-hidden="true" />
          )}
        </div>
      </motion.div>
    ))}
  </div>
);

/* -------------------------------------------------------------------- */
/* 03 · THE SIMULATOR                                                     */
/* -------------------------------------------------------------------- */
const Simulator = () => (
  <section
    id="ttk-app-simulator"
    data-testid="ttk-app-simulator-section"
    className="relative border-t border-line/60 bg-ink-2/40 px-6 py-24 md:px-12 md:py-32"
  >
    <div className="mx-auto max-w-[1500px]">
      <R>
        <p className="font-mono text-[11px] tracking-[0.28em] text-ash">
          02 · THE APP · PART ONE
        </p>
      </R>
      <div className="mt-6 grid items-center gap-16 lg:grid-cols-12">
        <R d={0.15} c="lg:col-span-6 lg:order-1">
          <PhonePlate
            src="/assets/app/app_metrics_top.webp"
            alt="The Simulator · wearables vs baseline and what-if"
            testid="ttk-simulator-phone"
            label="TRIFIT · METRICS · OPTIMISE"
            callouts={[
              {
                pos: { top: "36%", left: "-16rem" },
                side: "right",
                tag: "OPTIMISE",
                text: "WHAT IF I CHANGE X ?",
              },
              {
                pos: { bottom: "24%", left: "-16rem" },
                side: "right",
                tag: "ASK TWIN",
                text: "PROJECT THE IMPACT",
              },
            ]}
          />
        </R>
        <R d={0.05} c="lg:col-span-6 lg:order-2">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-md border border-signal/40 text-signal">
              <Sparkles className="h-4 w-4" strokeWidth={1.6} />
            </span>
            <p className="font-mono text-[10px] tracking-[0.24em] text-signal">
              THE SIMULATOR
            </p>
          </div>
          <h2
            data-testid="ttk-simulator-headline"
            className="mt-4 font-display text-3xl font-light leading-tight text-white md:text-5xl"
          >
            Try the decision{" "}
            <span className="text-signal">before</span> you make it.
          </h2>
          <p className="mt-5 max-w-lg text-sm leading-relaxed text-fog md:text-base">
            Push bedtime an hour earlier. Add resistance three times a week.
            Cut alcohol for a fortnight. Watch how the twin&apos;s projection
            shifts before you commit to any of it.
          </p>
          <ul className="mt-6 grid grid-cols-1 gap-3 text-xs text-fog md:text-sm">
            {[
              "Longevity index & wearable baselines side-by-side",
              "&quot;What if I sleep 30 more minutes?&quot;",
              "&quot;What if I drop weekly alcohol to zero?&quot;",
              "Every simulation feeds back into the Twin",
            ].map((f) => (
              <li
                key={f}
                className="border-l-2 border-line pl-3 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: f }}
              />
            ))}
          </ul>
        </R>
      </div>
    </div>
  </section>
);

/* -------------------------------------------------------------------- */
/* 04 · YOUR CUSTOM ROUTINE                                                */
/* -------------------------------------------------------------------- */
const Routine = () => (
  <section
    id="ttk-app-routine"
    data-testid="ttk-app-routine-section"
    className="relative border-t border-line/60 bg-ink-2/40 px-6 py-24 md:px-12 md:py-32"
  >
    <div className="mx-auto max-w-[1500px]">
      <R>
        <p className="font-mono text-[11px] tracking-[0.28em] text-ash">
          03 · THE APP · PART TWO
        </p>
      </R>
      <div className="mt-6 grid items-center gap-16 lg:grid-cols-12">
        <R d={0.15} c="lg:col-span-6 lg:order-1">
          <PhonePlate
            src="/assets/app/app_home_quicklog.webp"
            alt="Your custom routine · rank, effort/rest and quick log"
            testid="ttk-routine-phone"
            label="TRIFIT · HOME · YOUR ROUTINE"
            callouts={[
              {
                pos: { top: "18%", left: "-16rem" },
                side: "right",
                tag: "STREAK",
                text: "MULTIPLIER · 1.25×",
              },
              {
                pos: { top: "44%", left: "-16rem" },
                side: "right",
                tag: "RANK · B",
                text: "TODAY VS YESTERDAY",
              },
              {
                pos: { bottom: "12%", left: "-16rem" },
                side: "right",
                tag: "QUICK LOG",
                text: "ONE-TAP HABITS",
              },
            ]}
          />
        </R>
        <R d={0.05} c="lg:col-span-6 lg:order-2">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-md border border-signal/40 text-signal">
              <RouteIcon className="h-4 w-4" strokeWidth={1.6} />
            </span>
            <p className="font-mono text-[10px] tracking-[0.24em] text-signal">
              YOUR CUSTOM ROUTINE
            </p>
          </div>
          <h2
            data-testid="ttk-routine-headline"
            className="mt-4 font-display text-3xl font-light leading-tight text-white md:text-5xl"
          >
            A routine that <span className="text-signal">responds</span> to
            you.
          </h2>
          <ul className="mt-6 grid grid-cols-2 gap-3 text-xs text-fog md:text-sm">
            {[
              "Context-aware (travel · schedule · sleep)",
              "Optimised for your goals",
              "Daily rank with 7-day history",
              "Quick log · streak multiplier",
            ].map((f) => (
              <li
                key={f}
                className="border-l-2 border-line pl-3 leading-relaxed"
              >
                {f}
              </li>
            ))}
          </ul>
        </R>
      </div>
    </div>
  </section>
);

/* -------------------------------------------------------------------- */
/* Export all 4 app sub-sections wrapped                                   */
/* -------------------------------------------------------------------- */
const TtkApp = () => (
  <div data-testid="ttk-app-section">
    <Simulator />
    <Routine />
  </div>
);

export default TtkApp;
